/**
 * Login Page - Premium Professional Design
 *
 * Modern split-screen login interface with InvoSloth branding.
 * Shows business info from database and Hacienda integration badge.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { isAxiosError } from 'axios'

import { ExternalLink, FileCheck, Building2, Shield, Zap, BarChart3 } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { usePublicBusinessInfo } from '@/hooks/useBusinessInfo'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Logo } from '@/components/ui/Logo'
import { useToast } from '@/components/ui/toaster'
import { config } from '@/config'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data: business } = usePublicBusinessInfo()
  const currentYear = new Date().getFullYear()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await login(email, password)
      navigate('/')
    } catch (error) {
      const tooMany = isAxiosError(error) && error.response?.status === 429
      toast({
        title: t('login.authError'),
        description: tooMany ? t('login.tooManyAttempts') : t('login.invalidCredentials'),
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left Panel - Branding (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white p-12 flex-col justify-between relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-20 w-72 h-72 bg-amber-500 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-gray-500 rounded-full blur-3xl" />
        </div>

        {/* Content */}
        <div className="relative z-10">
          {/* Logo */}
          <div className="mb-12">
            <Logo size="xl" showText variant="glow" />
            <p className="text-gray-400 text-sm mt-2 ml-1">{config.product.description}</p>
          </div>

          {/* Business Name */}
          {business?.name && (
            <div className="mb-8 p-4 bg-white/5 rounded-lg border border-white/10">
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-amber-500" />
                <div>
                  <p className="font-semibold">{business.name}</p>
                  {business.identification && (
                    <p className="text-sm text-gray-400">{business.identification}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Features */}
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-300">
              {t('login.completeSystem')}
            </h2>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <FileCheck className="h-5 w-5 text-green-400" />
                </div>
                <div>
                  <p className="font-medium">{t('footer.haciendaVersion')}</p>
                  <p className="text-sm text-gray-400">
                    {t('footer.haciendaFullDesc')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Shield className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="font-medium">{t('login.secureReliable')}</p>
                  <p className="text-sm text-gray-400">
                    {t('login.secureReliableDesc')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <Zap className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <p className="font-medium">{t('login.fastEfficient')}</p>
                  <p className="text-sm text-gray-400">
                    {t('login.fastEfficientDesc')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <BarChart3 className="h-5 w-5 text-purple-400" />
                </div>
                <div>
                  <p className="font-medium">{t('login.completeReports')}</p>
                  <p className="text-sm text-gray-400">
                    {t('login.completeReportsDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 pt-8 border-t border-white/10">
          <div className="flex items-center justify-between text-sm text-gray-400">
            <div>
              <p className="mb-1">{t('footer.createdBy')}</p>
              <a
                href={config.creator.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-500 hover:text-amber-400 inline-flex items-center gap-1"
              >
                {config.creator.name}
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
            <p>&copy; {currentYear}</p>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <Logo size="2xl" variant="glow" className="mb-4" />
            <h1 className="text-2xl font-bold">
              <span className="text-foreground/80">Invo</span>
              <span className="text-amber-500/80 dark:text-amber-300/80">Sloth</span>
            </h1>
            <p className="text-sm text-muted-foreground">{config.product.description}</p>

            {/* Mobile Business Name */}
            {business?.name && (
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-muted rounded-full text-sm">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{business.name}</span>
              </div>
            )}
          </div>

          {/* Login Card */}
          <div className="bg-card rounded-xl border border-border shadow-lg p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-foreground">
                {t('login.title')}
              </h2>
              <p className="text-muted-foreground mt-2">
                {t('login.subtitle')}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-foreground">
                  {t('login.email')}
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('login.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-foreground">
                  {t('login.password')}
                </label>
                <Input
                  id="password"
                  type="password"
                  placeholder={t('login.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="h-12"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base font-medium"
                disabled={isLoading}
              >
                {isLoading ? t('login.loggingIn') : t('login.login')}
              </Button>
            </form>

            {/* Hacienda Badge */}
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center justify-center gap-3 text-sm">
                <FileCheck className="h-5 w-5 text-green-500" />
                <span className="text-muted-foreground">
                  {t('footer.haciendaVersion')} | {config.hacienda.country}
                </span>
              </div>
            </div>
          </div>

          {/* Mobile Footer */}
          <div className="lg:hidden mt-8 text-center">
            <p className="text-xs text-muted-foreground mb-1">
              {t('footer.createdBy')}
            </p>
            <a
              href={config.creator.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
            >
              {config.creator.name}
              <ExternalLink className="h-3 w-3" />
            </a>
            <p className="text-xs text-muted-foreground mt-2">
              &copy; {currentYear} {t('footer.copyright')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
